import React, { useMemo } from 'react';
import { Box, alpha, createTheme, ThemeProvider, useTheme } from '@mui/material';

// Scoped to school administration, including its portal-rendered dialogs. The
// parent palette, login, teacher portal and role/tenant guards are unchanged.
export default function InternalWorkspace({ children }) {
  const parentTheme = useTheme();
  const theme = useMemo(() => createTheme(parentTheme, {
    components: {
      MuiButton: {
        styleOverrides: {
          root: { '@media screen': {
            minHeight: 40, padding: '8px 16px', fontSize: '0.875rem', lineHeight: 1.5,
            borderRadius: 8, textTransform: 'none', boxShadow: 'none',
            transition: 'background-color 150ms ease, border-color 150ms ease',
            '&:hover': { transform: 'none', boxShadow: 'none' },
            '&.Mui-focusVisible': { outline: `2px solid ${parentTheme.palette.primary.main}`, outlineOffset: 2 },
          } },
          startIcon: { '& > *:nth-of-type(1)': { fontSize: 18 } },
          endIcon: { '& > *:nth-of-type(1)': { fontSize: 18 } },
        },
      },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiFormControl: { defaultProps: { size: 'small' } },
      MuiDialog: { styleOverrides: { paper: { '@media screen': {
        borderRadius: 16, maxWidth: 'calc(100% - 32px)',
        [parentTheme.breakpoints.down('sm')]: { margin: 16, width: 'calc(100% - 32px)' },
        '&.MuiDialog-paperFullScreen': { margin: 0, width: '100%', maxWidth: '100%', borderRadius: 0 },
      } } } },
      MuiDialogActions: { styleOverrides: { root: { '@media screen': {
        padding: 16, gap: 8, flexWrap: 'wrap', '& > :not(style) ~ :not(style)': { marginLeft: 0 },
        [parentTheme.breakpoints.down('sm')]: { '& > .MuiButton-root': { flex: '1 1 auto' } },
      } } } },
    },
  }), [parentTheme]);

  return (
    <ThemeProvider theme={theme}>
      <Box className="admin-workspace" sx={{
        minWidth: 0, width: '100%',
        '@media screen': {
          color: 'text.primary', maxWidth: 1800, mx: 'auto',
          '& > .MuiContainer-root': { maxWidth: 'none', p: 0, m: 0 },
          '& > .MuiBox-root:not(.workspace-overview)': { maxWidth: '100%', px: 0, py: 0 },
          '& .MuiGrid-root': { minWidth: 0 },
          '& .MuiCard-root, & .workspace-section': {
            boxShadow: '0 1px 3px rgba(15,23,42,0.04)', border: '1px solid', borderColor: 'divider', borderRadius: '14px',
            '&:hover': { boxShadow: '0 1px 3px rgba(15,23,42,0.04)', transform: 'none' },
          },
          '& .MuiCard-root .MuiCard-root': { boxShadow: 'none' },
          '& .MuiTableContainer-root': { boxShadow: 'none', maxWidth: '100%', overflowX: 'auto' },
          '& .MuiTable-root': { fontVariantNumeric: 'tabular-nums' },
          '& .MuiTableCell-head': {
            bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.035) : '#f8fafc',
            color: 'text.secondary', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.015em', py: 1.5, px: 2,
          },
          '& .MuiTableCell-body': { fontSize: '0.875rem', py: 1.5, px: 2 },
          '& .MuiTableCell-root:last-child .MuiButton-root': { minWidth: 0 },
          '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', px: 1, minHeight: 52 },
          '& .MuiTablePagination-spacer': { display: { xs: 'none', sm: 'block' } },
          '& .MuiTablePagination-actions': { ml: { xs: 0, sm: 2 } },
          '& .MuiButton-root': {
            minHeight: 40, py: 1, px: 2, fontSize: '0.875rem', lineHeight: 1.5, borderRadius: '8px', boxShadow: 'none',
            '&:hover': { transform: 'none', boxShadow: 'none' },
          },
          '& .MuiButton-startIcon > *, & .MuiButton-endIcon > *': { fontSize: 18 },
          '& .MuiIconButton-root': { minWidth: 36, minHeight: 36 },
          '& .MuiTabs-root': { borderBottom: '1px solid', borderColor: 'divider', minWidth: 0 },
          '& .MuiTab-root': { textTransform: 'none', minHeight: 44, fontWeight: 600 },
          '& .MuiOutlinedInput-root': { borderRadius: '8px', minHeight: 40 },
          '& .MuiOutlinedInput-root:not(.MuiInputBase-multiline) .MuiOutlinedInput-input': { py: 1.125 },
          '& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': { transform: 'translate(14px, 10px) scale(1)' },
          '& .workspace-section': { backgroundImage: 'none' },
          '& .workspace-filters .MuiTextField-root, & .workspace-filters .MuiFormControl-root': { minWidth: 0, maxWidth: '100%' },
          '& .workspace-filters > .MuiStack-root': { flexWrap: 'wrap', gap: 1.5, '& > :not(style) ~ :not(style)': { ml: 0 } },
          '& .workspace-filters .MuiTypography-h5, & .workspace-filters .MuiTypography-h6': { fontSize: '0.9375rem', fontWeight: 600 },
          '& .workspace-filters .MuiAvatar-root': { width: 28, height: 28 },
          '& .workspace-actions .MuiButton-root': { maxWidth: '100%', whiteSpace: 'normal' },
          '@media (prefers-reduced-motion: reduce)': { '& *, & *::before, & *::after': { transition: 'none !important', animation: 'none !important' } },
        },
        '@media print': { p: 0, m: 0, bgcolor: 'transparent' },
      }}>{children}</Box>
    </ThemeProvider>
  );
}
